// src/pages/Products.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMerchant } from '../context/MerchantContext';
import { listenToShopProducts, addProduct, updateProduct, deleteProduct, merchantLogout } from '../services/firebase';

const CATEGORIES = ['Vegetables', 'Fruits', 'Dairy', 'Grains', 'Snacks', 'Beverages', 'Household', 'Personal Care', 'Other'];

const emptyForm = { name: '', price: '', unit: 'kg', category: 'Vegetables', inStock: true, description: '' };

export default function Products() {
  const navigate = useNavigate();
  const { shop } = useMerchant();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!shop) return;
    const unsub = listenToShopProducts(shop.id, (data) => {
      setProducts(data);
      setLoading(false);
    });
    return unsub;
  }, [shop]);

  const handleEdit = (product) => {
    setForm({ name: product.name, price: product.price, unit: product.unit || 'kg', category: product.category || 'Vegetables', inStock: product.inStock !== false, description: product.description || '' });
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) { toast.error('Enter name and price'); return; }
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price), shopId: shop.id, shopName: shop.name };
      if (editingId) {
        await updateProduct(editingId, data);
        toast.success('Product updated! ✅');
      } else {
        await addProduct(data);
        toast.success('Product added! ✅');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
    } catch (e) {
      toast.error('Could not save product');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await deleteProduct(id);
      toast('Product deleted');
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const handleToggleStock = async (product) => {
    try {
      await updateProduct(product.id, { inStock: !product.inStock });
      toast(product.inStock ? 'Marked out of stock' : 'Back in stock! ✅');
    } catch (e) {
      toast.error('Could not update');
    }
  };

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', maxWidth: 900, margin: '0 auto' }}>

      {/* Topbar */}
      <div style={{ background: 'var(--dark)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', height: 60, gap: 16 }}>
        <div style={{ fontSize: 20 }}>🏪</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--green)' }}>{shop?.name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray)' }}>Merchant Dashboard</div>
        </div>
        {[
          { label: '📦 Orders', path: '/' },
          { label: '💰 Earnings', path: '/earnings' },
          { label: '🛒 Products', path: '/products', active: true },
          { label: '⚙️ Settings', path: '/settings' },
        ].map(n => (
          <button key={n.label} onClick={() => navigate(n.path)} style={{
            padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: n.active ? 'rgba(0,214,143,0.1)' : 'transparent',
            color: n.active ? 'var(--green)' : 'var(--gray)',
            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700,
          }}>{n.label}</button>
        ))}
        <button onClick={() => merchantLogout()} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 13 }}>Logout</button>
      </div>

      <div style={{ padding: 24 }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <input placeholder="🔍 Search products..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, maxWidth: 300 }} />
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 13, color: 'var(--gray)' }}>{products.length} products • {products.filter(p => p.inStock !== false).length} in stock</div>
          <button className="btn btn-green" style={{ width: 'auto', padding: '10px 20px' }} onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}>
            + Add Product
          </button>
        </div>

        {/* Products grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--gray)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>No products yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Add your first product to get started</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {filtered.map(product => (
              <div key={product.id} className="card" style={{ padding: 16, opacity: product.inStock === false ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{product.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{product.category}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--green)' }}>₹{product.price}</div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 12 }}>per {product.unit}</div>

                {/* Stock toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '8px 10px', background: 'var(--dark)', borderRadius: 8 }}>
                  <span style={{ fontSize: 12, color: product.inStock !== false ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                    {product.inStock !== false ? '✓ In Stock' : '✕ Out of Stock'}
                  </span>
                  <div onClick={() => handleToggleStock(product)} style={{ width: 36, height: 20, borderRadius: 10, background: product.inStock !== false ? 'var(--green)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: product.inStock !== false ? 18 : 2, transition: 'left 0.2s' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEdit(product)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>✏️ Edit</button>
                  <button onClick={() => handleDelete(product.id, product.name)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: 'var(--red)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>🗑 Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, animation: 'fadeIn 0.2s ease' }}>
          <div className="card" style={{ width: '100%', maxWidth: 440, padding: 28, animation: 'slideUp 0.2s ease' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>{editingId ? 'Edit Product' : 'Add New Product'}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>PRODUCT NAME</div>
                <input placeholder="e.g. Tomatoes" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>PRICE (₹)</div>
                <input type="number" placeholder="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>UNIT</div>
                <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} style={{ width: '100%', background: 'var(--card)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 14px', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 14, outline: 'none' }}>
                  {['kg', 'g', 'litre', 'ml', 'piece', 'dozen', 'bunch', 'packet'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>CATEGORY</div>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ width: '100%', background: 'var(--card)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 14px', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 14, outline: 'none' }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>DESCRIPTION (optional)</div>
                <textarea placeholder="Brief description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ height: 72, resize: 'none' }} />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div onClick={() => setForm({ ...form, inStock: !form.inStock })} style={{ width: 44, height: 24, borderRadius: 12, background: form.inStock ? 'var(--green)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: form.inStock ? 22 : 2, transition: 'left 0.2s' }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: form.inStock ? 'var(--green)' : 'var(--gray)' }}>In Stock</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
              <button className="btn btn-green" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" /> : editingId ? 'Save Changes' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
